/**
 * Token estimation utilities for ludicrous-gateway
 * Provides fast token count estimation without calling tokenizer APIs
 */

import type { Message, ContentBlock, TextContent } from "../types/messages.js";

/**
 * Average characters per token ratio
 * This is an approximation - actual tokenization varies by model
 */
const CHARS_PER_TOKEN = 4;

/**
 * Overhead tokens for message structure
 */
const MESSAGE_OVERHEAD_TOKENS = 4;

/**
 * Overhead tokens for tool definitions
 */
const TOOL_OVERHEAD_TOKENS = 10;

/**
 * Extract text from a content block
 */
function extractTextFromBlock(block: ContentBlock): string {
  switch (block.type) {
    case "text":
      return block.text;
    case "tool_use":
      return `${block.name} ${JSON.stringify(block.input)}`;
    case "tool_result":
      if (typeof block.content === "string") {
        return block.content;
      }
      return block.content.map(extractTextFromBlock).join(" ");
    case "image":
      // Images contribute tokens based on dimensions
      // Use a conservative estimate
      return " ".repeat(1000);
    default:
      return "";
  }
}

/**
 * Extract all text from message content
 */
function extractTextFromContent(content: string | ContentBlock[]): string {
  if (typeof content === "string") {
    return content;
  }
  return content.map(extractTextFromBlock).join(" ");
}

/**
 * Estimate tokens from a text string
 */
export function estimateTokensFromText(text: string): number {
  if (!text) return 0;

  // Simple estimation: character count / average chars per token
  const baseTokens = Math.ceil(text.length / CHARS_PER_TOKEN);

  // Add some overhead for whitespace and special characters
  const whitespaceCount = (text.match(/\s+/g) ?? []).length;
  const specialCharCount = (text.match(/[{}[\](),:;"'`]/g) ?? []).length;

  return baseTokens + Math.ceil(whitespaceCount / 10) + Math.ceil(specialCharCount / 5);
}

/**
 * Estimate tokens for a single message
 */
export function estimateTokensFromMessage(message: Message): number {
  const textContent = extractTextFromContent(message.content);
  return estimateTokensFromText(textContent) + MESSAGE_OVERHEAD_TOKENS;
}

/**
 * Estimate tokens for a system prompt
 */
export function estimateTokensFromSystem(
  system: string | ContentBlock[] | undefined
): number {
  if (!system) return 0;

  if (typeof system === "string") {
    return estimateTokensFromText(system) + MESSAGE_OVERHEAD_TOKENS;
  }

  const text = system
    .filter((b): b is TextContent => b.type === "text")
    .map((b) => b.text)
    .join(" ");

  return estimateTokensFromText(text) + MESSAGE_OVERHEAD_TOKENS;
}

/**
 * Token estimation result
 */
export interface TokenEstimate {
  /** Total estimated tokens */
  total: number;
  /** Tokens from system prompt */
  system: number;
  /** Tokens from messages */
  messages: number;
  /** Tokens from tools */
  tools: number;
}

/**
 * Estimate total tokens for a messages request
 */
export function estimateRequestTokens(request: {
  messages: Message[];
  system?: string | ContentBlock[];
  tools?: unknown[];
}): TokenEstimate {
  const systemTokens = estimateTokensFromSystem(request.system);

  const messageTokens = request.messages.reduce(
    (sum, msg) => sum + estimateTokensFromMessage(msg),
    0
  );

  // Tools contribute to token count based on their schema
  const toolTokens = (request.tools ?? []).length * TOOL_OVERHEAD_TOKENS;

  return {
    total: systemTokens + messageTokens + toolTokens,
    system: systemTokens,
    messages: messageTokens,
    tools: toolTokens,
  };
}

/**
 * Get total text length from a request (for simple size checking)
 */
export function getRequestTextLength(request: {
  messages: Message[];
  system?: string | ContentBlock[];
}): number {
  let length = 0;

  // System prompt
  if (request.system) {
    if (typeof request.system === "string") {
      length += request.system.length;
    } else {
      for (const block of request.system) {
        if (block.type === "text") {
          length += block.text.length;
        }
      }
    }
  }

  // Messages
  for (const message of request.messages) {
    const content = extractTextFromContent(message.content);
    length += content.length;
  }

  return length;
}
